// One sensor: its name, and which of two states it is in.
//
// The card is the DeviceCard's shape — same ButtonColor, same radius 20, same
// star and liveness badge — so a sensor sits in the same visual family as the
// devices above it. What is different is the middle: instead of the device
// artwork it carries a red/green disc and the sensor's own words, because that
// IS the device. There is nothing else to show and nothing to press.
//
// ⚠️ GREY IS A REAL STATE, and it is not green. Until a status arrives the disc
// is neutral and reads "No report yet". Painting an unheard-from door sensor
// green is the one failure that matters here: it says "closed" about a door
// nobody has heard from.
//
// The colours are fixed, not themed. Server-driven theming can put any hex in
// ButtonColor, and a red/green pair that the customer's own palette washes out
// would defeat the point of the card.

import { useApp } from '../../../core/state/AppContext';
import React from 'react';
import {
  Box, Card, CardActionArea, IconButton, Typography, useTheme,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LivenessBadge from './LivenessBadge';
import AutoHorizontalScroll from './AutoHorizontalScroll';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import {
  sensorStateOf, sensorSilence, silenceText, SENSOR_ALERT, SENSOR_NORMAL,
} from '../../../core/devices/sensorState';
import { ageOf } from '../../../core/devices/deviceStatus';
import { parseServerTime } from '../../../core/devices/firmwareInfo';

const COLOURS = {
  [SENSOR_NORMAL]: '#2E7D32',
  [SENSOR_ALERT]: '#C62828',
};
const NEUTRAL = '#9E9E9E';

export default function SensorCard({
  device, onOpen, onToggleFavourite, showLocation = false,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const uno = device.UniqueNumber;
  // One answer for every star: the app's favourites list (AppContext).
  const { isFavourite } = useApp();
  const isFav = isFavourite ? isFavourite(device) : Number(device.favourite) === 1;
  // Seeded from the row so the card is right on first paint, then live: the
  // server's column and the MQTT frame are the same string in two shapes, and
  // sensorStateOf takes either.
  const [status, , liveAt] = useDeviceStatus(uno, device.Status ?? '');
  const { state, label } = sensorStateOf(status, device.DeviceID ?? device.DeviceId);
  // Silent for hours: the state is LAST KNOWN, drawn grey with its age.
  const silent = sensorSilence(device, liveAt, parseServerTime, ageOf);
  const colour = silent ? NEUTRAL : (COLOURS[state] || NEUTRAL);
  const name = device.DeviceName || device.DeviceTypeName || 'Sensor';

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Card elevation={6} sx={{ bgcolor: c.button, borderRadius: '20px', height: '100%' }}>
        <CardActionArea
          onClick={() => onOpen && onOpen(device)}
          sx={{
            height: '100%',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: colour,
                // The ring keeps the disc readable on a ButtonColor that happens
                // to be close to either colour.
                boxShadow: `0 0 0 3px ${colour}33, 0 1px 4px rgba(0,0,0,0.4)`,
              }}
            />
            <Box sx={{ height: 10 }} />
            <Typography
              sx={{
                color: c.text,
                fontSize: 13,
                fontWeight: 700,
                textAlign: 'center',
                lineHeight: 1.2,
                textTransform: 'uppercase',
              }}
            >
              {silent ? `Last: ${label}` : label}
            </Typography>
            {silent && (
              <Typography sx={{ color: c.text, opacity: 0.7, fontSize: 11, textAlign: 'center', lineHeight: 1.2 }}>
                {`not heard for ${silenceText(silent)}`}
              </Typography>
            )}
            <Box sx={{ height: 6 }} />
            <AutoHorizontalScroll text={name} />
            {/* Favourites only — a starred sensor is out of its room's context
                and needs to say where it is, exactly as DeviceCard does. */}
            {showLocation && (
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', mt: 0.5,
              }}
              >
                <LocationOnIcon sx={{ fontSize: 16, color: c.text }} />
                <Box sx={{ width: 4 }} />
                <AutoHorizontalScroll
                  text={`${device.LocationName || 'No Location'} > ${device.RoomName || 'Common Device'}`}
                />
              </Box>
            )}
          </Box>
        </CardActionArea>
      </Card>

      <Box sx={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
        <LivenessBadge uniqueNumber={uno} />
      </Box>

      {onToggleFavourite && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onToggleFavourite(device, isFav); }}
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            p: 0.75,
            bgcolor: '#fff',
            boxShadow: '0 0 4px rgba(0,0,0,0.26)',
            '&:hover': { bgcolor: '#fff' },
          }}
        >
          <StarIcon sx={{ fontSize: 20, color: isFav ? '#FFC107' : c.back }} />
        </IconButton>
      )}
    </Box>
  );
}
